/**
 * Salon-booking state container.
 *
 * One reducer keeps every selection (cart, master, slot, client) in a
 * single tree so child components can subscribe via `useSalonBooking()`
 * without prop-drilling through the layout. The reducer also enforces a
 * few invariants the UI relies on:
 *
 *   - removing the only service in the cart auto-resets master/slot
 *   - changing the master invalidates the previously-chosen slot
 *   - choosing a slot under "any master" mode hydrates the
 *     `assignedMasterId` so the rest of the flow has a concrete master
 *
 * Persistence is deliberately out of scope here — the booking is short
 * enough that we don't need to survive page refreshes.
 */

import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useReducer,
} from "react";

const ANY_MASTER = "any";

const initialState = {
  cart: [],
  masterId: ANY_MASTER,
  slot: null,
  client: { name: "", phone: "", notes: "" },
  booking: null,
  bookingState: "idle",
  bookingError: null,
};

function reducer(state, action) {
  switch (action.type) {
    case "addService": {
      if (state.cart.find((c) => c.id === action.service.id)) return state;
      return {
        ...state,
        cart: [...state.cart, action.service],
        slot: null,
      };
    }
    case "removeService": {
      const nextCart = state.cart.filter((c) => c.id !== action.serviceId);
      return {
        ...state,
        cart: nextCart,
        slot: null,
        masterId: nextCart.length === 0 ? ANY_MASTER : state.masterId,
      };
    }
    case "toggleService": {
      const exists = state.cart.find((c) => c.id === action.service.id);
      if (exists) {
        const nextCart = state.cart.filter((c) => c.id !== action.service.id);
        return {
          ...state,
          cart: nextCart,
          slot: null,
          masterId: nextCart.length === 0 ? ANY_MASTER : state.masterId,
        };
      }
      return { ...state, cart: [...state.cart, action.service], slot: null };
    }
    case "addServices": {
      const nextCart = [...state.cart];
      for (const service of action.services || []) {
        if (!nextCart.find((c) => c.id === service.id)) {
          nextCart.push(service);
        }
      }
      return { ...state, cart: nextCart, slot: null };
    }
    case "setMaster": {
      if (state.masterId === action.masterId) return state;
      return { ...state, masterId: action.masterId, slot: null };
    }
    case "setSlot":
      return { ...state, slot: action.slot };
    case "updateClient":
      return { ...state, client: { ...state.client, ...action.patch } };
    case "bookingStart":
      return { ...state, bookingState: "creating", bookingError: null };
    case "bookingSuccess":
      return {
        ...state,
        bookingState: "success",
        booking: action.booking,
        bookingError: null,
      };
    case "bookingError":
      return { ...state, bookingState: "error", bookingError: action.error };
    case "resetBooking":
      return initialState;
    case "resetForNewBooking":
      return {
        ...initialState,
        client: state.client,
      };
    default:
      return state;
  }
}

const SalonBookingContext = createContext(null);

export function SalonBookingProvider({ children }) {
  const [state, dispatch] = useReducer(reducer, initialState);

  const totalDuration = useMemo(
    () => state.cart.reduce((sum, s) => sum + (s.duration || 0), 0),
    [state.cart],
  );
  const totalPrice = useMemo(
    () => state.cart.reduce((sum, s) => sum + (s.price || s.priceFrom || 0), 0),
    [state.cart],
  );
  const hasOpenPriceFrom = useMemo(
    () => state.cart.some((s) => s.priceFrom != null && s.price == null),
    [state.cart],
  );

  const toggleService = useCallback((service) => {
    dispatch({ type: "toggleService", service });
  }, []);
  const addServices = useCallback((services) => {
    dispatch({ type: "addServices", services });
  }, []);
  const removeService = useCallback((serviceId) => {
    dispatch({ type: "removeService", serviceId });
  }, []);
  const setMaster = useCallback((masterId) => {
    dispatch({ type: "setMaster", masterId });
  }, []);
  const setSlot = useCallback((slot) => {
    dispatch({ type: "setSlot", slot });
  }, []);
  const updateClient = useCallback((patch) => {
    dispatch({ type: "updateClient", patch });
  }, []);
  const startBooking = useCallback(() => dispatch({ type: "bookingStart" }), []);
  const succeedBooking = useCallback(
    (booking) => dispatch({ type: "bookingSuccess", booking }),
    [],
  );
  const failBooking = useCallback(
    (error) => dispatch({ type: "bookingError", error }),
    [],
  );
  const reset = useCallback(() => dispatch({ type: "resetBooking" }), []);
  const resetForNewBooking = useCallback(
    () => dispatch({ type: "resetForNewBooking" }),
    [],
  );

  const value = useMemo(
    () => ({
      ...state,
      totalDuration,
      totalPrice,
      hasOpenPriceFrom,
      ANY_MASTER,
      toggleService,
      addServices,
      removeService,
      setMaster,
      setSlot,
      updateClient,
      startBooking,
      succeedBooking,
      failBooking,
      reset,
      resetForNewBooking,
    }),
    [
      state,
      totalDuration,
      totalPrice,
      hasOpenPriceFrom,
      toggleService,
      addServices,
      removeService,
      setMaster,
      setSlot,
      updateClient,
      startBooking,
      succeedBooking,
      failBooking,
      reset,
      resetForNewBooking,
    ],
  );

  return (
    <SalonBookingContext.Provider value={value}>
      {children}
    </SalonBookingContext.Provider>
  );
}

export function useSalonBooking() {
  const ctx = useContext(SalonBookingContext);
  if (!ctx) {
    throw new Error("useSalonBooking must be used inside <SalonBookingProvider>");
  }
  return ctx;
}

export { ANY_MASTER };
